import React, {
  useEffect,
  useCallback,
  useRef,
} from 'react';
import gsap from 'gsap';
import { useInView } from 'react-intersection-observer';
import ScrollTrigger from 'gsap/ScrollTrigger';
import DrawSVGPlugin from 'gsap/DrawSVGPlugin';
import { SplitWordsAnimation, Section } from './common';
import { HorizontalLine } from './common/Lines';
import Sources from './Sources';
import './End.scss';

gsap.registerPlugin(ScrollTrigger, DrawSVGPlugin);

const vh = coef => window.innerHeight * (coef / 100);

const End = () => {
  const lineScrollTriggerRef = useRef();
  const thankYouScrollTriggerRef = useRef();
  const lineAnimationRef = useRef();
  const thankYouAnimationRef = useRef();
  const lineSvgRef = useRef();
  const thankYouSvgRef = useRef();

  const [titleRef, titleInView] = useInView({ threshold: 0.2, triggerOnce: true });

  const initLineScrollTrigger = useCallback(() => {
    const tl = gsap.timeline();
    const scrollTrigger = ScrollTrigger.create({
      trigger: lineSvgRef.current,
      pin: false,
      once: true,
      start: `top-=${vh(30)}`,
      end: `bottom-=${vh(60)}`,
      scrub: 0,
      animation: tl,
    });
    tl.fromTo(lineSvgRef.current, {
      drawSVG: '0%',
    }, {
      drawSVG: '100%',
      ease: 'none',
    }, 0);
    lineAnimationRef.current = tl;
    lineScrollTriggerRef.current = scrollTrigger;
  }, []);

  const initThankYouScrollTrigger = useCallback(() => {
    const tl = gsap.timeline();
    const scrollTrigger = ScrollTrigger.create({
      trigger: thankYouSvgRef.current,
      pin: false,
      once: true,
      start: `top-=${vh(60)}`,
      end: `bottom-=${vh(30)}`,
      scrub: 0,
      animation: tl,
    });
    tl.fromTo(thankYouSvgRef.current, {
      drawSVG: '100% 100%',
    }, {
      drawSVG: '0% 100%',
      ease: 'none',
    }, 0);
    thankYouAnimationRef.current = tl;
    thankYouScrollTriggerRef.current = scrollTrigger;
  }, []);

  useEffect(() => {
    if (lineSvgRef.current) {
      initLineScrollTrigger();
      return () => {
        lineAnimationRef.current.kill();
        lineScrollTriggerRef.current.kill();
      };
    }
  }, [lineSvgRef]);

  useEffect(() => {
    if (thankYouSvgRef.current) {
      initThankYouScrollTrigger();
      return () => {
        thankYouAnimationRef.current.kill();
        thankYouScrollTriggerRef.current.kill();
      };
    }
  }, [thankYouSvgRef]);

  return (
    <>
      <Section className="end-line-drawing">
        <div className="line-svg">
          <svg className="svg-line" preserveAspectRatio="none" viewBox="0 0 560 759" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path ref={lineSvgRef} d="M280 0V759" />
          </svg>
        </div>
        <div className="thank-you-svg">
          <svg viewBox="0 0 560 312" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path ref={thankYouSvgRef} d="M146.895 267c3.5-4.833 11.7-15.3 16.5-18.5 4.8-3.2 6 1.333 6 4 .5 3.833-.5 11.7-8.5 12.5-8 .8-11.666-6.667-12.5-10.5-1.5-1.667-3.6-3.4 0 3 4.5 8 15.5 6.5 20 5.5s18.5-5.5 24-9.5 4.5-20.5-2.5-13.5-13 20-6 25c5.6 4 19-2.667 25-6.5-2.5 3.667-6.6 10.1-3 6.5 3.6-3.6 10.167-14.833 13-20 0 1.333.6 4.6 3 7 2.4 2.4 4.334 1 5 0-.666 0-2.4 1.6-4 8-2 8 6 4.5 9.5 4s10.5-11.5 9-5.5-.432 6.5 4.5 9c4.933 2.5 13-23 7-22.5-4.8.4-7.333 4.833-8 7-3 5.167-5.8 14.9 1 14.5 8.5-.5 6.5-10 9-14 2-3.2-.166-6.667-1.5-8-2.833.333-8.2 2.1-7 6.5 1.5 5.5 18 4.5 21 3.5s7.5-5.5 13-13 10.5-19 3-15-17.5 36.5-13 38.5 13-1.5 17-3 11.834-12.333 15.5-17.5c3.334-7.167 12.3-20.8 5.5-18-8.5 3.5-15 28-15 32s0 5 6.5 6.5c5.2 1.2 16.834-10.333 21.5-15-.833.5-3.7 5.6-4.5 12-1 8 13 3 15.5 3 2 0 4.167-9.333 5-14 .167 3.667-.1 15.3-.5 12.5-.4-2.8 11.5-16.5 15-14.5s-1 6-1 9.5 4 7.5 6.5 6.5 18.167-10.667 25-17.5c-.166-.333-2.1-.9-8.5-.5-8 .5-16 12.5-10 15 4.8 2 14-7.833 18-13-8.333 23-27.1 65.6-35.5 52-8.4-13.6 14.834-27 27.5-32 .334-2 4.1-7.4 16.5-13s39-13 52-22.5 30-40.5 25-45.5-13.5 5-17.5-1c-3.335-5.003 1.5-7.333 4-8 1.834 0 3.9.3-2.5 1.5s-8.333-4.833-8.5-8l-13.5 20.5c3-4.167 8.4-12.2 6-11-3 1.5-20.5 1-21.5-3s6-10 7.5-5 1 15.5-4.5 19-7.5 2-10-1.5 9-19 13-19c3.2 0 3 4.333 2.5 6.5-.666 4.167-4.2 13.2-13 16-8.8 2.8-22-9.5-27.5-16-1.884 0-5.423 1.3-4.5 6.5.924 5.2 7.718 2.167 11 0 2.5-.333 9.1-3.7 15.5-14.5 8-13.5 3-19 1-19-1.6 0-23 43.333-33.5 65-3.833 5.5-9.2 16.3 0 15.5 11.5-1 15.5-16 19-26.5s-4-25.5-11-27c-5.6-1.2-12.666 4.833-15.5 8-.666 1.167-4.2 4-13 6s-3.666-11.167 0-18c-3.5 6.333-12.3 19-19.5 19-7.2 0-1.666-12.667 2-19 .667.667.4 3-6 7s-16.333-.087-20.5-3.421c-2.5 5-6.5 14.021-2.5 16.421 4 2.4 8.667-3.333 10.5-6.5 1.834-5.333 4.4-16 0-16s-7.833 5.333-9 8c-2.333 4.167-8.1 12.5-12.5 12.5-5.5 0-15.5 8.5-23 15.5-6 5.6-6.5 15.333-6 19.5-.666.667-.4 1 6-3 8-5 27-58 25-50s-17.5 20-22 14.5c-3.6-4.4-.5-10.833 1.5-13.5-2.166 0-7.7.9-12.5 4.5-6 4.5-10.5 7.5-21 12.5-8.4 4-12.666-5.061-12.5-9.395 2.167 0 7 2.895 11.5-.105s6.5-6.999-2-8.5c-8.5-1.501-15.5 22-15.5 20s4.5-16.502 6-24.001c1.5-7.499 10.5-23.999 12.5-17.999 1.6 4.8-6 19.333-10 26-2.666 4.333-10.5 13-13 14s-5.5 1-7-3.5 3.5-14-2.5-14.5-10 19-11 19 4-16 2-19-2.5 14.5-6 18c-2.8 2.8-9.166.5-12-1 1.667-8.333 3.4-24.4-3-22-8 3-14.5 16.5-13.5 22 .8 4.4 10.667.167 15.5-2.5 1.667-7.833 3.2-22.2-4-17-9 6.5-9 14-12.5 18.5-2.8 3.6-10.5 4.167-14 4 0 0 4-12.5 2-14.5s-4-.5-8 3-11 13.5-11 13.5 8.5-14.5 11-21 1.5-3.5 3.5-8.5 4-13 2-13.5 1 2.5-4 8-21 9-31 5.5-17.5 7.277-2.5 5 11.5-5.5 19-10-6 31-13.5 34.5-23.5-3-29-7.5c-4.4-3.6-8.833-13.834-10.5-18.501 0 0-5.5-11.999-2.5-29 3-17.001 9-22.499 27-27.999s88.5-8.5 116-20.5 61-46 61-77.5V0" />
          </svg>
        </div>
      </Section>
      <Section className="end-text-section" hasGrid>
        <div className="title">
          <div className="title-container" ref={titleRef}>
            <SplitWordsAnimation animationDelay={0} show={titleInView}>
              <h2>Notes</h2>
            </SplitWordsAnimation>
          </div>
        </div>
        <div className="column column-left">
          <p>In one universe I’m a quantum physicist. But not in  this universe, the information found on this website should by no means be used as a credible source.</p>
          <p>In another universe, I’m a writer. But not in this universe, most of the information in this page was taken from other articles. They can be found in the list below.</p>
          <p>In yet another universe, I just presented all the information you just read in the form of an interpretive dance. But, alas, unfortunately this is not that universe.</p>
        </div>
        <div className="column column-right">
          <p>In this universe my name is Allan and I am a developer. I was inspired to create this page after reading Dark Matter by Blake Crouch.</p>
          <p>Dark Matter embeds the Many Worlds Interpretation into a story and explores what might take place if we were to interact with our alternate timelines.</p>
          <p>Some of what you have read on this page are quotes taken from the novel. I recommend reading the entire thing, and I hope you enjoy it as much as I did.</p>
        </div>
        <HorizontalLine className="section-divider" transformOrigin="0 50%" />
        <div className="column column-left">
          <p>
            <em>
              Everyone has regrets, everyone has done things they wish they hadn’t, everyone wonders what might have happened if things had gone another way. The many-worlds interpretation consoles us and wards us off all at the same time.
            </em>
          </p>
        </div>
        <div className="column column-right">
          <p>
            <em>
              I’m rich. I’m a movie star. I’m king of the world. I’m also poor. I’m homeless. Lots of me are dead.
            </em>
          </p>
          <p>
            <em>
              I’m none of these. Not in this universe. But in the multiverse I’m all of them, and more.
            </em>
          </p>
        </div>
        <HorizontalLine className="section-divider" transformOrigin="100% 50%" />
      </Section>
      <Sources />
    </>
  );
};

export default End;
